import type { SyncOp } from '@ims/core'

// Wire contract between the local sync engine (packages/local) and the server
// (implemented with supabase-js in packages/remote). Shapes mirror the RPCs in
// supabase/migrations/20260927000600_sync.sql and ..._tenant_rpcs.sql.

export interface PushTx {
  tx_id: string
  ops: SyncOp[]
}

export interface PushResult {
  tx_id: string | null
  status: 'applied' | 'duplicate' | 'rejected'
  code?: string
  message?: string
}

export interface PullCursor {
  xid: string
  tbl: string
  id: string
}

export interface PullResponse {
  changes: { table: string; row: Record<string, unknown> }[]
  cursor: PullCursor
  has_more: boolean
}

export interface Entitlements {
  tenant_id: string
  plan_id: string | null
  status: string | null
  current_period_end: string | null
  grace_until: string | null
  limits: Record<string, number | null>
  can_write: boolean
  issued_at: string
  /** From device_checkin: the server's time when the device checked in. */
  server_now?: string
  /** From device_checkin: the month's finalized invoices, as the plan limit counts them. */
  invoices_this_month?: number
}

export interface RegisteredCounter {
  counter: Record<string, unknown>
  sequences: Record<string, unknown>[]
}

export interface SyncTransport {
  /** One sync_push call. Throws SyncError for whole-call failures. */
  push(tenantId: string, installationId: string, txs: PushTx[]): Promise<PushResult[]>
  pull(tenantId: string, cursor: PullCursor | null, limit: number): Promise<PullResponse>
  /** Current server rows by id (missing ids = deleted or never synced). */
  fetchRows(table: string, ids: string[]): Promise<Record<string, unknown>[]>
  /** Checks this device in (device_checkin) and returns the tenant's entitlements. */
  entitlements(tenantId: string, installationId: string): Promise<Entitlements>
  registerCounter(
    tenantId: string,
    installationId: string,
    name?: string,
  ): Promise<RegisteredCounter>
  /** Realtime "something changed" notifications for the tenant. */
  subscribe(tenantId: string, onChange: () => void): () => void
}

/**
 * A whole-call failure. `retryable` errors (network, timeouts, expired
 * sessions) are retried with backoff; the others stop sync until the user acts.
 */
export class SyncError extends Error {
  readonly code: string
  readonly retryable: boolean

  constructor(code: string, message: string, retryable: boolean) {
    super(`${code}: ${message}`)
    this.name = 'SyncError'
    this.code = code
    this.retryable = retryable
  }
}
